import { PASSWORD_RULES, passwordIsValid } from '@/lib/validation'

/**
 * Live rule feedback for signup. Rules render unmet-but-neutral until the user
 * types, so an untouched form never looks like a wall of errors.
 *
 * The marker is a squared cell that fills green when the rule is met — the same
 * "arrived" signal the board uses everywhere else, at the smallest size it is
 * still legible.
 */
export function PasswordRules({ value }: { value: string }) {
  const touched = value.length > 0

  return (
    <div>
      <ul className="grid gap-1.5 sm:grid-cols-3">
        {PASSWORD_RULES.map((rule) => {
          const met = rule.test(value)
          return (
            <li
              key={rule.id}
              className={`flex items-center gap-2 text-[11px] ${
                met ? 'text-ok' : touched ? 'text-muted' : 'text-faint'
              }`}
            >
              <span
                aria-hidden="true"
                className={`grid size-4 shrink-0 place-items-center rounded-[2px] border ${
                  met ? 'border-ok/50 bg-ok-dim' : 'border-line-strong'
                }`}
              >
                {met ? (
                  <svg viewBox="0 0 12 12" className="size-2.5" fill="none">
                    <path
                      d="M2.5 6.2 4.8 8.5 9.5 3.8"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : null}
              </span>
              {rule.label}
            </li>
          )
        })}
      </ul>
      <p aria-live="polite" className="sr-only">
        {touched
          ? passwordIsValid(value)
            ? 'Password meets all requirements'
            : `Password still needs: ${PASSWORD_RULES.filter((rule) => !rule.test(value))
                .map((rule) => rule.label.toLowerCase())
                .join(', ')}`
          : ''}
      </p>
    </div>
  )
}
